import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { AnalyticsEventKey } from 'utils/analytics/constants';

import { ABSOLUTE_URLS } from '../../../constants';
import { logAnalyticsEvent } from '../../../utils/analytics';
import { LOGIN_CONFIRMATION_FORM } from '../constants';

export interface LoginConfirmationEventLabels {
  clickstreamViewed?: string;
  clickstreamLogo?: string;
  clickstreamClosed?: string;
}

const ANALYTICS_KEY: AnalyticsEventKey = LOGIN_CONFIRMATION_FORM;

interface LoginConfirmationExtraProperties {
  program?: string;
  flow: string;
}

interface LoginConfirmationActions {
  eventsExtraProperties?: LoginConfirmationExtraProperties;
  logMemberExitLoginConfirmation: () => void;
  exitLoginConfirmation: () => void;
}

const useLoginConfirmationActions = (
  flow: string,
  program?: string,
  eventLabels?: LoginConfirmationEventLabels,
  closeTarget?: string
): LoginConfirmationActions => {
  const router = useRouter();
  const eventsExtraProperties = useMemo(
    () => ({ flow, program }),
    [flow, program]
  );
  const logAnalytics = useCallback(
    (label: string) =>
      logAnalyticsEvent({
        name: ANALYTICS_KEY,
        label,
        properties: eventsExtraProperties,
      }),
    [eventsExtraProperties]
  );

  const { clickstreamViewed, clickstreamLogo, clickstreamClosed } =
    eventLabels ?? {};

  useEffect(() => {
    if (clickstreamViewed) {
      logAnalytics(clickstreamViewed);
    }
  }, [clickstreamViewed, logAnalytics]);

  const logMemberExitLoginConfirmation = () => {
    if (clickstreamLogo) {
      logAnalytics(clickstreamLogo);
    }
  };

  const exitLoginConfirmation = () => {
    if (clickstreamClosed) {
      logAnalytics(clickstreamClosed);
    }
    router.push(closeTarget ?? ABSOLUTE_URLS.ACCOUNT_SUB);
  };

  return {
    eventsExtraProperties,
    logMemberExitLoginConfirmation,
    exitLoginConfirmation,
  };
};

export default useLoginConfirmationActions;
